/**
 * Hono server lifecycle orchestrator (NEX-346 Tarea 4).
 *
 * Drop-in replacement for `core/server.ts` over Hono. Coexists with the
 * Express-based `core/server.ts` until Tarea 7 retires it. The full pipeline
 * (logger, DB, migrations, seeds, module/plugin registration, HTTP listen,
 * Socket.IO, tunnel, lifecycle hooks, graceful shutdown) is preserved 1:1.
 */

import type { Server as HttpServer } from 'node:http'
import { join } from 'node:path'

import { entityRoom, DEFAULT_LOCALES } from '@gzl10/nexus-sdk'

import { createApp } from './app.js'
import { setShuttingDown } from './lifecycle-state.js'
import { createRawHttpServer, startHttpServer, stopHttpServer, getHttpServer } from './server.js'
import { initSocketIO, closeSocketIO } from './websocket/index.js'
import { resetServeSPA } from './spa-handler.js'
import { initErrorMiddleware } from './middleware/error.middleware.js'
import type { HonoApp } from './types.js'

import { setCustomCaslRules, clearCustomCaslRules, setSeedPermissions, clearSeedPermissions } from '@kernel/abilities/ability.factory.js'
import { getDatabaseType, getDatabasePath } from '@kernel/config/database.js'
import { resolveConfig, resetConfig, env } from '@kernel/config/env.js'
import { getOrCreateInstanceInfo } from '@kernel/config/instance-id.js'
import { loadNexusConfig, resetConfigCache } from '@kernel/config/load-config.js'
import { getLibPath, getProjectPath } from '@kernel/config/paths.js'
import type { NexusConfig, StartOptions } from '@kernel/config/types.js'
import { setSecretResolverDb, initAuthSecret, _resetSecretResolver } from '@kernel/crypto/secret-resolver.js'
import {
  destroyDb,
  getDb,
  runModuleSeed,
  ensureSystemTables,
  runAllGeneratedMigrations,
  buildMigrationSources,
  loadAllMigrationFiles,
  createMemoryTables
} from '@kernel/db/index.js'
import { ensureMigrationTables } from '@kernel/db/ensure-system-tables.js'
import { detectSchemaDrift, formatDriftMessage } from '@kernel/db/migration-generator.js'
import { runMigrations } from '@kernel/db/migration-runner.js'
import {
  loadCoreModules,
  getOrderedModules,
  getOrderedModulesInternal,
  resetStore,
  registerPlugin,
  registerModule,
  topologicalSortPlugins,
  shouldLoadPlugin,
  resetSharedAdapters,
  resetCacheManager,
  setLocales,
  getSubjectForTable
} from '@kernel/engine/index.js'
import type { PluginManifest } from '@kernel/engine/index.js'
import { defineAbilityFor } from '@kernel/abilities/ability.factory.js'
import type { Subjects } from '@kernel/abilities/ability.types.js'
import { createModuleContext } from '@kernel/engine/context.js'
import { eventBridge } from '@kernel/events/event-bridge.js'
import { nexusEvents } from '@kernel/events/emitter.js'
import {
  getLoggerConfig,
  initLoggerService,
  getPinoLogger,
  captureExceptionSafe,
  logger,
  setLoggerInstance
} from '@kernel/logger/index.js'
import { validateWorkflowConfig, injectWorkflowField } from '@kernel/runtime/workflow/inject-workflow-field.js'

import { CORE_MODULES } from '../modules/index.js'
import { setTokenCreateHook } from '../modules/auth/auth-hooks.js'
import { buildEffectiveCorsOrigins } from '../core/utils/cors.js'
import { checkPortAvailable } from '../core/utils/port-check.js'
import { startTunnel, stopTunnel, getTunnelUrl } from '../core/tunnel.js'

let app: HonoApp | null = null
let currentConfig: StartOptions | undefined
let gracefulShutdownRegistered = false

type LifecycleState = 'idle' | 'starting' | 'running' | 'stopping' | 'stopped'
let lifecycleState: LifecycleState = 'idle'
let startupPromise: Promise<HttpServer> | null = null
let tunnelStartupPromise: Promise<unknown> | null = null

/** Run a cleanup step without ever throwing — logs the failure and continues. */
async function safeCleanup(label: string, fn: () => unknown | Promise<unknown>): Promise<void> {
  try {
    await fn()
  } catch (err) {
    try { logger.warn({ err, step: label }, 'Cleanup step failed (continuing)') } catch { /* logger gone */ }
  }
}

export type { NexusConfig, StartOptions }

async function runMigrationsAndSeeds(config?: StartOptions): Promise<void> {
  // Inicializar logger antes de cualquier log para evitar múltiples instancias de pino-pretty
  initLoggerService(getLoggerConfig())
  setLoggerInstance(getPinoLogger())

  // Initialize error middleware with exception capture (injected to avoid core/ → modules/ dependency)
  initErrorMiddleware({ captureException: captureExceptionSafe })

  // Reset store to ensure idempotent start (prevents module duplication in tests)
  resetStore()

  // Cargar módulos core antes de usarlos
  loadCoreModules()

  logger.info({ type: getDatabaseType(), path: getDatabasePath() }, 'Database ready')

  // Asegurar tablas de sistema ANTES de consultar plugins
  await ensureSystemTables(getDb())

  // Initialize auth secret (env → DB → auto-generate)
  setSecretResolverDb(getDb())
  await initAuthSecret(logger)

  // Warning: SQLite + PM2 cluster mode
  const isPM2Cluster = !!(process.env['PM2_HOME'] && process.env['NODE_APP_INSTANCE'])
  if (isPM2Cluster && getDatabaseType() === 'sqlite') {
    logger.warn('SQLite detected with PM2 cluster mode. SQLite does not support concurrent writes reliably. Use PostgreSQL for multi-process deployments, or set PM2 instances to 1.')
  }

  // Load config file + auto-discovered plugins, merge with programmatic config
  const fileConfig = await loadNexusConfig()
  const effectiveConfig: StartOptions = { ...config }

  // Merge plugins: programmatic take precedence, file/discovered are appended
  const programmaticPluginNames = new Set((config?.plugins ?? []).map((p) => p.name))
  const additionalPlugins = (fileConfig.plugins ?? []).filter((p) => !programmaticPluginNames.has(p.name))
  if (additionalPlugins.length > 0) {
    effectiveConfig.plugins = [...(config?.plugins ?? []), ...additionalPlugins]
    logger.info({ count: additionalPlugins.length }, 'Plugins merged from config/discovery')
  }

  // Merge modules: same pattern, but also exclude core modules (both registered and intentionally skipped)
  const programmaticModuleNames = new Set((config?.modules ?? []).map((m) => m.name))
  const coreModuleNames = new Set(CORE_MODULES.map((m) => m.name))
  const additionalModules = (fileConfig.modules ?? []).filter(
    (m) => !programmaticModuleNames.has(m.name) && !coreModuleNames.has(m.name)
  )
  if (additionalModules.length > 0) {
    effectiveConfig.modules = [...(config?.modules ?? []), ...additionalModules]
  }

  // Filtrar plugins según nexus.plugins.json
  const pluginsToLoad: PluginManifest[] = []
  for (const plugin of effectiveConfig?.plugins ?? []) {
    if (shouldLoadPlugin(plugin.name)) {
      pluginsToLoad.push(plugin)
      logger.debug({ plugin: plugin.name }, 'Plugin enabled')
    } else {
      logger.info({ plugin: plugin.name }, 'Plugin disabled, skipping')
    }
  }

  // Registrar solo plugins habilitados (ordenados por dependencias)
  const orderedPlugins = topologicalSortPlugins(pluginsToLoad)
  for (const plugin of orderedPlugins) {
    registerPlugin(plugin)
    logger.debug({ plugin: plugin.name }, 'Plugin registered')
  }

  // Registrar módulos sueltos del usuario
  for (const mod of effectiveConfig?.modules ?? []) {
    registerModule(mod, { source: 'standalone' })
    logger.debug({ module: mod.name }, 'Standalone module registered')
  }

  // Inject workflow fields before drift check / migration generation.
  const allModules = getOrderedModulesInternal()
  for (const mod of allModules) {
    for (const def of mod.definitions ?? []) {
      const workflow = (def as { workflow?: import('@gzl10/nexus-sdk').WorkflowConfig }).workflow
      if (!workflow) continue
      const entityName = ('table' in def ? (def as { table: string }).table : undefined) ?? mod.name
      validateWorkflowConfig(workflow, entityName)
      const fields = (def as { fields: Record<string, unknown> }).fields
      injectWorkflowField(fields, workflow)
    }
  }

  const ctx = createModuleContext()
  const modules = getOrderedModules()

  // Asegurar tablas de migración
  await ensureMigrationTables(getDb())

  // === Migration handling ===
  if (env.NODE_ENV === 'test') {
    logger.debug('Test mode — creating tables from entity definitions...')
    await runAllGeneratedMigrations(ctx, modules)
  } else if (env.NODE_ENV === 'development') {
    const sources = buildMigrationSources()
    const migrationFiles = await loadAllMigrationFiles(sources)

    if (migrationFiles.length > 0) {
      logger.info({ sources: sources.length, files: migrationFiles.length }, 'Running migration deploy...')
      try {
        await runMigrations(undefined, sources)
      } catch (err) {
        logger.fatal({ err }, 'Migration deploy failed')
        captureExceptionSafe(err as Error, { scope: 'db', action: 'migrations', phase: 'dev-deploy' })
        throw err
      }
    }

    logger.debug('Checking schema drift...')
    const drift = await detectSchemaDrift()
    if (drift && (drift.newTables.length > 0 || drift.alteredTables.length > 0)) {
      const message = formatDriftMessage(drift)
      logger.fatal(message)
      const driftError = new Error('Schema drift detected. Run "nexus migrate:create" and "nexus migrate:deploy" to update.')
      captureExceptionSafe(driftError, { scope: 'db', action: 'schema-drift', phase: 'dev-check' })
      throw driftError
    }
    if (drift?.droppedTables.length) {
      logger.warn({ tables: drift.droppedTables }, 'Extra tables found in database (not managed by current entities)')
    }
    logger.debug('No schema drift detected')
  } else {
    const sources = buildMigrationSources()
    const migrationFiles = await loadAllMigrationFiles(sources)

    if (migrationFiles.length === 0) {
      const dirs = sources.map((s) => `  ${s.id}: ${s.dir}`).join('\n')
      logger.fatal('No migration files found')
      const noMigrationsError = new Error(`No migration files found. Generate migrations first with the CLI.\nSearched in:\n${dirs}`)
      captureExceptionSafe(noMigrationsError, { scope: 'db', action: 'migrations', phase: 'deploy' })
      throw noMigrationsError
    }

    logger.info({ count: migrationFiles.length, sources: sources.map((s) => s.id) }, 'Deploying migrations...')
    try {
      await runMigrations(undefined, sources)
    } catch (err) {
      logger.fatal({ err }, 'Migration deploy failed')
      captureExceptionSafe(err instanceof Error ? err : new Error(String(err)), { scope: 'db', action: 'migrations', phase: 'deploy' })
      throw err
    }

    const drift = await detectSchemaDrift()
    if (drift && (drift.newTables.length > 0 || drift.alteredTables.length > 0)) {
      const message = formatDriftMessage(drift)
      logger.fatal(message)
      const postDeployDriftError = new Error('Schema drift detected after migration deploy. Some migrations may be missing.')
      captureExceptionSafe(postDeployDriftError, { scope: 'db', action: 'schema-drift', phase: 'post-deploy' })
      throw postDeployDriftError
    }
    if (drift?.droppedTables.length) {
      logger.warn({ tables: drift.droppedTables }, 'Extra tables found in database (not managed by current entities)')
    }
  }

  // Create in-memory tables for entities with adapter: 'memory'
  const allDefinitions = modules.flatMap(m => m.definitions ?? [])
  await createMemoryTables(allDefinitions)

  // Import user seed files from data/seeds/ (before module seeds so plugins respect if-empty)
  try {
    const { importSeedFiles } = await import('../cli/seed-commands.js')
    await importSeedFiles(ctx.db.knex, modules, {
      info: (msg: string) => logger.info(msg),
      debug: (msg: string) => logger.debug(msg),
    })
  } catch (err) {
    logger.debug({ err }, 'Seed file import skipped (no data/seeds/ or error)')
  }

  logger.debug('Running seeds...')
  for (const mod of modules) {
    try {
      await runModuleSeed(mod, ctx)
    } catch (err) {
      logger.error({ module: mod.name, err }, 'Seed failed - continuing with next module')
    }
  }

  // User-defined seed hook (runs after all module seeds)
  if (config?.onSeed) {
    const { getPlugins } = await import('@kernel/engine/module-queries.js')
    const { createMasterRegistry } = await import('../modules/masters/registry.js')

    const masterRegistry = ctx.services.has('masters')
      ? ctx.services.get('masters') as { register: (...args: any[]) => void; seed: (ctx: any) => Promise<void> }
      : createMasterRegistry()

    const pluginPrefixes = new Map<string, string>()
    for (const plugin of getPlugins()) {
      pluginPrefixes.set(plugin.code, `${plugin.code}_`)
      const shortName = plugin.name.replace(/^@[^/]+\/nexus-plugin-/, '')
      if (shortName !== plugin.code) {
        pluginPrefixes.set(shortName, `${plugin.code}_`)
      }
    }

    const { createSeedContext } = await import('@kernel/db/seed-context.js')
    const { ctx: seedCtx, flushPermissions } = createSeedContext({
      knex: ctx.db.knex,
      generateId: ctx.core.generateId,
      hashPassword: ctx.core.crypto.hashPassword,
      masterRegistry,
      pluginPrefixes,
      logger: ctx.core.logger,
      onPermissionsCollected: (perms) => setSeedPermissions(perms)
    })

    await config.onSeed(seedCtx)

    await masterRegistry.seed(ctx)

    flushPermissions()
  }
}

/**
 * Starts the Nexus server on top of Hono.
 *
 * @returns The underlying `http.Server` (so Socket.IO and tests can attach).
 * @throws If the server is already running (call `stop()` first).
 */
export async function start(config?: StartOptions): Promise<HttpServer> {
  if (startupPromise) return startupPromise
  if (app || getHttpServer()) {
    throw new Error('Server already running. Call stop() first.')
  }

  lifecycleState = 'starting'
  const promise = (async () => {
    try {
      const srv = await startInternal(config)
      lifecycleState = 'running'
      return srv
    } catch (err) {
      await safeCleanup('startup-failure-cleanup', () => stop())
      throw err
    } finally {
      // Clear the memo once we settle, so subsequent start() calls hit the
      // "already running" guard (or retry a clean startup) instead of
      // returning this already-resolved promise.
      startupPromise = null
    }
  })()
  startupPromise = promise
  return promise
}

async function startInternal(config?: StartOptions): Promise<HttpServer> {
  currentConfig = config
  setShuttingDown(false)

  setLocales(config?.locales ?? DEFAULT_LOCALES)

  if (env.NODE_ENV === 'development' && env.FRPC_SERVER && !env.TRUST_PROXY) {
    process.env['TRUST_PROXY'] = 'true'
  }

  const resolved = resolveConfig()

  const dataDir = join(getProjectPath(), 'data')
  const instanceInfo = await getOrCreateInstanceInfo(dataDir)
  logger.info(`Instance ID: ${instanceInfo.instanceId}`)

  if (resolved.port > 0 && env.NODE_ENV !== 'development') {
    await checkPortAvailable(resolved.port, resolved.host)
  }

  if (config?.events) {
    for (const [eventName, handler] of Object.entries(config.events)) {
      if (handler && typeof handler === 'function') {
        nexusEvents.on(eventName as any, handler)
        logger.debug(`Event handler registered: ${eventName}`)
      }
    }
  }

  if (config?.casl) {
    setCustomCaslRules(config.casl)
    logger.debug('Custom CASL rules registered')
  }

  if (config?.onTokenCreate) {
    setTokenCreateHook(config.onTokenCreate)
    logger.debug('Custom JWT claims hook registered')
  }

  nexusEvents.emitEvent('server.starting', { port: resolved.port, host: resolved.host })

  const { initTelemetry } = await import('../instrumentation.js')
  await initTelemetry()

  await runMigrationsAndSeeds(config)

  const effectiveCorsOrigins = buildEffectiveCorsOrigins(env.CORS_ORIGIN, config?.spas)

  // Pre-create the raw HTTP server so Vite can share it for HMR WebSocket upgrades.
  // Must happen before createApp() because mountViteDevMiddleware attaches the
  // 'upgrade' event listener to this server via hmr: { server: httpServer }.
  // Pattern mirrors the legacy Express server (memory #975).
  const rawHttpServer = createRawHttpServer()

  app = await createApp({
    beforeRoutes: config?.beforeRoutes,
    afterRoutes: config?.afterRoutes,
    spas: config?.spas,
    httpServer: rawHttpServer,
  })

  // Attach Hono's request listener to the pre-created server now that the app is ready.
  const { getRequestListener } = await import('@hono/node-server')
  rawHttpServer.on('request', getRequestListener(app.fetch))

  // Listen with retry in dev mode (handles --watch restart race condition)
  const maxRetries = env.NODE_ENV === 'development' ? 10 : 1
  const retryDelay = 300
  let actualPort = resolved.port
  let listened = false

  for (let attempt = 1; attempt <= maxRetries && !listened; attempt++) {
    try {
      await new Promise<void>((resolve, reject) => {
        let settled = false
        const httpInstance = startHttpServer(app!, {
          port: resolved.port,
          hostname: resolved.host,
          existingServer: rawHttpServer,
          onListen: (info) => {
            if (settled) return
            settled = true
            actualPort = info.port
            resolve()
          }
        })
        // Attach error listener to the underlying http.Server so EADDRINUSE rejects.
        const httpSrv = httpInstance as unknown as HttpServer
        httpSrv.once('error', (err) => {
          if (settled) return
          settled = true
          reject(err)
        })
      })
      listened = true
    } catch (err: any) {
      if (err?.code === 'EADDRINUSE' && attempt < maxRetries) {
        logger.warn({ attempt, maxRetries, port: resolved.port }, 'Port in use, retrying...')

        // Cleanup the failed serve() reference before retrying
        await stopHttpServer().catch(() => {})

        // Politely SIGTERM stale node/tsx procs holding the port (zombie from
        // --watch restart). Avoid SIGKILL: it produces non-zero exit codes that
        // node --watch reports as "Failed running" — making clean restarts look
        // like crashes. SIGTERM lets the prior process flush and exit 0.
        try {
          const { execSync } = await import('node:child_process')
          // -sTCP:LISTEN restricts to actual listeners (not client sockets).
          const pids = execSync(`lsof -iTCP:${resolved.port} -sTCP:LISTEN -t 2>/dev/null`, { encoding: 'utf-8' }).trim()
          for (const pidStr of pids.split('\n').filter(Boolean)) {
            const zombiePid = Number(pidStr)
            if (zombiePid && zombiePid !== process.pid) {
              try {
                const comm = execSync(`ps -o comm= -p ${zombiePid} 2>/dev/null`, { encoding: 'utf-8' }).trim()
                if (!comm.includes('node') && !comm.includes('tsx')) continue
              } catch { continue }
              try {
                process.kill(zombiePid, 'SIGTERM')
                logger.warn({ zombiePid, port: resolved.port }, 'Sent SIGTERM to stale process on port')
              } catch { /* already dead */ }
            }
          }
        } catch { /* lsof failed — port may have been freed */ }

        await new Promise(r => setTimeout(r, retryDelay))
      } else {
        await stopHttpServer().catch(() => {})
        app = null
        throw err
      }
    }
  }

  const httpSrv = getHttpServer()!

  // Permanent error handler (post-listen socket errors)
  httpSrv.on('error', (err) => {
    logger.fatal({ err }, 'HTTP server error')
    captureExceptionSafe(err, { scope: 'server', action: 'http-error' })
  })

  // Request timeout
  const timeoutMs = parseInt(process.env['REQUEST_TIMEOUT_MS'] || '30000', 10)
  if (timeoutMs > 0) {
    httpSrv.setTimeout(timeoutMs)
  }

  // Socket.IO
  initSocketIO(httpSrv, {
    corsOrigin: effectiveCorsOrigins,
    canSubscribeEntity: async (roleIds, _module, entity) => {
      const subject = getSubjectForTable(entity)
      if (!subject) return false
      const ability = await defineAbilityFor({ id: '_socket_subscribe_' }, roleIds)
      return ability.can('read', subject as Subjects)
    }
  })

  // Event bridge default rules + init
  eventBridge.registerRule({
    nexusEvent: 'entity.created',
    socketEvent: 'entity:created',
    target: { type: 'room', room: (p: any) => entityRoom(p.tenantId, p.module, p.entity) },
    debounceMs: 50
  })
  eventBridge.registerRule({
    nexusEvent: 'entity.updated',
    socketEvent: 'entity:updated',
    target: { type: 'room', room: (p: any) => entityRoom(p.tenantId, p.module, p.entity) },
    debounceMs: 50
  })
  eventBridge.registerRule({
    nexusEvent: 'entity.deleted',
    socketEvent: 'entity:deleted',
    target: { type: 'room', room: (p: any) => entityRoom(p.tenantId, p.module, p.entity) }
  })
  eventBridge.init()

  // Resolve actual port (when port: 0 was requested)
  const addr = httpSrv.address()
  if (typeof addr === 'object' && addr) {
    actualPort = addr.port
  }

  // Start frpc tunnel in development — non-blocking
  if (env.NODE_ENV === 'development' && env.FRPC_SERVER && env.FRPC_SUBDOMAIN) {
    const tunnelSubdomain = env.FRPC_SUBDOMAIN
    const tunnelServer = env.FRPC_SERVER
    const tunnelPublicHost = env.FRPC_PUBLIC_HOST ?? tunnelServer
    tunnelStartupPromise = startTunnel({
      server: tunnelServer,
      serverPort: env.FRPC_SERVER_PORT,
      token: env.FRPC_TOKEN,
      subdomain: tunnelSubdomain,
      localPort: actualPort,
      publicHost: env.FRPC_PUBLIC_HOST
    }).then(active => {
      if (active && !process.env['BACKEND_URL']) {
        process.env['BACKEND_URL'] = getTunnelUrl(tunnelSubdomain, tunnelPublicHost)
      }
    }).catch(err => {
      logger.warn({ err }, 'Tunnel startup failed (non-fatal)')
    })
  }

  const baseUrl = env.BACKEND_URL || `http://localhost:${actualPort}`
  logger.debug({ libPath: getLibPath(), projectPath: getProjectPath() }, 'Paths')
  const urls: Record<string, string> = { api: `${baseUrl}/api/v1` }
  if (resolved.ui.enabled) urls['ui'] = baseUrl
  logger.info({ port: actualPort, mode: resolved.nodeEnv, ...urls }, 'Server started')
  nexusEvents.emitEvent('server.started', { port: actualPort, host: resolved.host })

  if (config?.onReady) {
    try {
      await config.onReady(app, actualPort, resolved.host)
      logger.debug('onReady hook executed')
    } catch (err) {
      logger.error({ err }, 'Error in onReady hook')
    }
  }

  return httpSrv
}

/**
 * Stops the Hono server gracefully. Idempotent.
 */
export async function stop(): Promise<void> {
  // Wait for any in-flight startup so we don't tear down half-initialized state.
  if (startupPromise) {
    try { await startupPromise } catch { /* startup failed — proceed to cleanup */ }
  }
  if (tunnelStartupPromise) {
    try { await tunnelStartupPromise } catch { /* already logged */ }
  }

  lifecycleState = 'stopping'
  setShuttingDown(true)

  // Release port FIRST so a restarting watcher can rebind even if cleanup is slow.
  await safeCleanup('http-server', () => stopHttpServer().catch((err) => {
    if ((err as NodeJS.ErrnoException).code !== 'ERR_SERVER_NOT_RUNNING') throw err
  }))

  await safeCleanup('emit-stopping', () => nexusEvents.emitEvent('server.stopping'))

  if (currentConfig?.beforeClose) {
    await safeCleanup('beforeClose-hook', () => currentConfig!.beforeClose!())
  }

  await safeCleanup('tunnel', () => stopTunnel())
  await safeCleanup('cache-manager', () => resetCacheManager())
  await safeCleanup('event-bridge', () => eventBridge.destroy())
  await safeCleanup('socket-io', () => closeSocketIO())

  await safeCleanup('telemetry', async () => {
    const { shutdownTelemetry } = await import('../instrumentation.js')
    await shutdownTelemetry()
  })

  await safeCleanup('db', () => destroyDb())
  await safeCleanup('emit-db-disconnected', () => nexusEvents.emitEvent('db.disconnected'))

  await safeCleanup('config', () => resetConfig())
  await safeCleanup('store', () => resetStore())
  await safeCleanup('shared-adapters', () => resetSharedAdapters())
  await safeCleanup('config-cache', () => resetConfigCache())
  await safeCleanup('casl-rules', () => clearCustomCaslRules())
  await safeCleanup('token-hook', () => setTokenCreateHook(undefined))
  await safeCleanup('seed-permissions', () => clearSeedPermissions())
  await safeCleanup('secret-resolver', () => _resetSecretResolver())
  await safeCleanup('spa', () => resetServeSPA())

  currentConfig = undefined
  app = null
  startupPromise = null
  tunnelStartupPromise = null
  lifecycleState = 'stopped'

  await safeCleanup('emit-stopped', () => nexusEvents.emitEvent('server.stopped'))
  try { logger.info('Server stopped') } catch { /* logger gone */ }
}

/**
 * Restarts the server. Reuses previous config if none provided.
 */
export async function restart(config?: StartOptions): Promise<HttpServer> {
  const configToUse = config ?? currentConfig

  nexusEvents.emitEvent('server.restarting')
  await stop()

  return start(configToUse)
}

/** Returns `true` if the server is currently running. */
export function isRunning(): boolean {
  return getHttpServer() !== null
}

// Graceful shutdown handlers
function setupGracefulShutdown(): void {
  if (gracefulShutdownRegistered) return
  gracefulShutdownRegistered = true

  let shuttingDown = false

  const shutdown = async (signal: string): Promise<void> => {
    if (shuttingDown) return
    shuttingDown = true
    setShuttingDown(true)

    try { logger.info({ signal }, 'Graceful shutdown initiated') } catch { /* logger gone */ }

    // Stop frpc FIRST so SIGTERM can flush closeProxy. Best-effort.
    try { stopTunnel() } catch { /* ignore */ }

    // Drain keep-alive connections without killing in-flight requests:
    // - Reduce keepAliveTimeout so idle sockets recycle fast (default is ~5s).
    // - closeIdleConnections() kills only idle keep-alive sockets (Node v18.2+).
    // - close() stops accepting new connections and resolves once active ones drain.
    // Sockets with requests in progress are left alone; the shutdown middleware
    // tags their responses with `Connection: close` so clients drop them after.
    try {
      const httpSrv = getHttpServer()
      if (httpSrv) {
        httpSrv.keepAliveTimeout = 1000
        httpSrv.headersTimeout = 1500
        if (typeof httpSrv.closeIdleConnections === 'function') {
          httpSrv.closeIdleConnections()
        }
        httpSrv.close(() => {})
      }
    } catch { /* ignore */ }

    const timeoutMs = parseInt(process.env['SHUTDOWN_TIMEOUT_MS'] || '10000', 10)
    const timer = setTimeout(() => {
      try { logger.warn('Graceful shutdown timed out, forcing exit') } catch { /* ignore */ }
      // Exit 1: a 10s timeout IS a crash (cleanup failed). Watchers relaunch
      // on non-zero too, and k8s/PM2/systemd need the signal to alert/escalate.
      process.exit(1)
    }, timeoutMs)
    timer.unref()

    // stop() is now fully tolerant — it never throws.
    await stop()
    clearTimeout(timer)
    process.exit(0)
  }

  process.on('SIGTERM', () => shutdown('SIGTERM'))
  process.on('SIGINT', () => shutdown('SIGINT'))
  process.on('SIGHUP', () => shutdown('SIGHUP'))

  process.on('uncaughtException', (err) => {
    try { logger.fatal({ err }, 'Uncaught exception — initiating shutdown') } catch { /* ignore */ }
    try { captureExceptionSafe(err, { scope: 'process', action: 'uncaughtException' }) } catch { /* ignore */ }
    void shutdown('uncaughtException')
  })

  process.on('unhandledRejection', (reason) => {
    try { logger.fatal({ err: reason }, 'Unhandled rejection — initiating shutdown') } catch { /* ignore */ }
    try { captureExceptionSafe(reason instanceof Error ? reason : new Error(String(reason)), { scope: 'process', action: 'unhandledRejection' }) } catch { /* ignore */ }
    void shutdown('unhandledRejection')
  })

  // Last-resort diagnostics: sync writes so the message survives even if pino
  // transport buffers were flushed/closed. Helps trace "process died silently"
  // cases (typical: node --watch eager kill, OTel beforeExit, vite worker).
  process.on('beforeExit', (code) => {
    if (code !== 0 && lifecycleState === 'running') {
      try { process.stderr.write(`[nexus] beforeExit code=${code} state=${lifecycleState} — event loop drained unexpectedly\n`) } catch { /* ignore */ }
    }
  })
  process.on('exit', (code) => {
    if (code !== 0 && lifecycleState !== 'stopped') {
      try { process.stderr.write(`[nexus] exit code=${code} state=${lifecycleState}\n`) } catch { /* ignore */ }
    }
  })
}

setupGracefulShutdown()
